/**
 * The loading scene (#loading): index.html draws it and loading/loading.css styles it before the
 * game's code has even downloaded; this drives it once the code runs. It shows the bar and the
 * percentage of work actually done, the step under way, where its files come from (the cache in
 * this browser or a download, cache/loading.ts), gameplay tips and lines of the legend in
 * turn, where the player is heading, a retry button when a download fails, and fades into the game
 * (and back over it, to enter the world or change zone).
 */
import { NINE_SWORDS } from '@nine/shared';

/** Where a zone change is heading, kept across the reload for index.html to show at once. */
const DEST_KEY = 'nine-swords.loading.v1';

/** Short gameplay tips (from the help window's rules). */
const TIPS: readonly string[] = [
  'เคล็ดลับ: การโจมตีขัดจังหวะศัตรูที่กำลังชาร์จท่าทำลาย',
  'เคล็ดลับ: ท่าทำลายทำให้การ์ดของศัตรูที่ตั้งรับแตกและมึนงง',
  'เคล็ดลับ: ตั้งรับไว้ การโจมตีจะเหลือเพียง 10% แต่ระวังท่าทำลาย',
  'เคล็ดลับ: วงแดงบนพื้นคือท่าทำลายที่ตั้งรับไม่ได้ ให้ใช้วิชาตัวเบาหลบ',
  'เคล็ดลับ: นั่งสมาธิ (V) ได้แต้มฝึกปรือมากกว่ายืนเฉยสี่เท่า',
  'เคล็ดลับ: การฝึกวิชายังดำเนินต่อแม้ปิดเกมไปแล้ว',
  'เคล็ดลับ: กด H เพื่อดูวิธีเล่นและอ่านตำนานกระบี่ทั้งเก้า',
];

/** Lines of the legend (data/lore.ts): what each of the nine swords taught. */
const LORE: readonly string[] = NINE_SWORDS.map((s) => `“${s.lesson}” · ${s.name}`);

const TIP_MS = 5200;
const FADE_MS = 450;

export interface LoadingView {
  progress(fraction: number): void;
  step(text: string): void;
}

export class LoadingScene implements LoadingView {
  private readonly root: HTMLElement | null = document.getElementById('loading');
  private readonly fill = this.part('[data-fill]');
  private readonly bar = this.part('.ls-bar');
  private readonly pct = this.part('[data-pct]');
  private readonly stepEl = this.part('[data-step]');
  private readonly srcEl = this.part('[data-src]');
  private readonly tipEl = this.part('[data-tip]');
  private readonly destEl = this.part('[data-dest]');
  private readonly errorEl = this.part('[data-error]');
  private readonly lines: string[];
  private tipAt = 0;
  private tipTimer: ReturnType<typeof setInterval> | undefined;
  private shownPct = -1;

  constructor() {
    // Tips and legend lines interleaved, from a random place so a reload shows something new.
    this.lines = TIPS.flatMap((t, i) => [t, LORE[i % LORE.length]!]);
    this.tipAt = Math.floor(Math.random() * this.lines.length);
    if (this.isShown) this.startTips();
  }

  private part(sel: string): HTMLElement | null {
    return this.root?.querySelector<HTMLElement>(sel) ?? null;
  }

  get isShown(): boolean {
    return !!this.root && !this.root.hidden && !this.root.classList.contains('done');
  }

  /** Real progress, 0–1 (never shown going backwards within one load). */
  progress(fraction: number): void {
    const p = Math.max(0, Math.min(100, Math.floor(fraction * 100)));
    if (p === this.shownPct) return;
    this.shownPct = p;
    if (this.fill) this.fill.style.width = `${p}%`;
    if (this.pct) this.pct.textContent = `${p}%`;
    this.bar?.setAttribute('aria-valuenow', String(p));
  }

  step(text: string): void {
    if (this.stepEl && this.stepEl.textContent !== text) this.stepEl.textContent = text;
  }

  /** Where the step's files come from: the cache in this browser, or a download (null hides it). */
  source(text: string | null): void {
    if (!this.srcEl) return;
    if (this.srcEl.textContent !== (text ?? '')) this.srcEl.textContent = text ?? '';
    this.srcEl.hidden = !text;
  }

  /** Where the player is going, as shown (index.html shows a zone change's before the code runs). */
  get dest(): string | null {
    return this.destEl && !this.destEl.hidden ? this.destEl.textContent : null;
  }

  /** The line naming where the player is going (null hides it). */
  destination(text: string | null): void {
    if (!this.destEl) return;
    this.destEl.textContent = text ?? '';
    this.destEl.hidden = !text;
  }

  /**
   * Shows the scene over the game from an empty bar: faded in over the world (a zone change), or
   * at once (`fade` false) where what it covers has already gone (the character select).
   */
  show(dest: string | null = null, fade = true): void {
    const root = this.root;
    if (!root) return;
    this.shownPct = -1;
    this.progress(0);
    this.destination(dest);
    this.source(null);
    this.clearError();
    root.hidden = false;
    root.classList.remove('done');
    root.classList.toggle('ls-in', fade);
    root.setAttribute('aria-busy', 'true');
    this.startTips();
  }

  /** Fades the scene out into what it covered; resolves once it is gone. */
  hide(): Promise<void> {
    const root = this.root;
    this.stopTips();
    clearDestination();
    this.source(null);
    if (!root || !this.isShown) return Promise.resolve();
    this.progress(1);
    root.setAttribute('aria-busy', 'false');
    root.classList.remove('ls-in');
    root.classList.add('done');
    return new Promise((done) =>
      setTimeout(() => {
        // Kept (hidden) for the next time: entering the world from the front, or a zone change.
        if (root.classList.contains('done')) root.hidden = true;
        done();
      }, FADE_MS),
    );
  }

  /**
   * A download failed: says so, and offers to try again (`retry`). `detail` says what failed (the
   * step and the file), under the message.
   */
  fail(message: string, retry: () => void, detail: string | null = null): void {
    const root = this.root;
    if (!root || !this.errorEl) return;
    root.hidden = false;
    root.classList.remove('done');
    root.dataset.state = 'error';
    const text = this.errorEl.querySelector('[data-error-text]');
    if (text) text.textContent = message;
    this.showDetail(detail);
    this.errorEl.hidden = false;
    const btn = this.errorEl.querySelector<HTMLButtonElement>('[data-retry]');
    if (btn) {
      btn.onclick = () => {
        this.clearError();
        retry();
      };
      btn.focus();
    }
  }

  /** A message that cannot be fixed by trying again (no WebGL): no retry button. */
  fatal(message: string, detail: string | null = null): void {
    this.stopTips();
    const root = this.root;
    if (!root) return;
    root.hidden = false;
    root.classList.remove('done');
    root.dataset.state = 'error';
    const text = this.errorEl?.querySelector('[data-error-text]');
    if (text) text.textContent = message;
    this.showDetail(detail);
    if (this.errorEl) this.errorEl.hidden = false;
    const btn = this.errorEl?.querySelector<HTMLButtonElement>('[data-retry]');
    if (btn) btn.hidden = true;
  }

  /**
   * The failure's details line; the line of where files came from goes, as it is about the load
   * that stopped, not about what failed.
   */
  private showDetail(detail: string | null): void {
    this.source(null);
    const el = this.errorEl?.querySelector<HTMLElement>('[data-error-detail]');
    if (!el) return;
    el.textContent = detail ?? '';
    el.hidden = !detail;
  }

  private clearError(): void {
    if (this.errorEl) this.errorEl.hidden = true;
    if (this.root) delete this.root.dataset.state;
  }

  private startTips(): void {
    if (this.tipTimer !== undefined || !this.tipEl) return;
    const el = this.tipEl;
    el.textContent = this.lines[this.tipAt % this.lines.length]!;
    this.tipTimer = setInterval(() => {
      el.classList.add('out');
      setTimeout(() => {
        this.tipAt++;
        el.textContent = this.lines[this.tipAt % this.lines.length]!;
        el.classList.remove('out');
      }, 300);
    }, TIP_MS);
  }

  private stopTips(): void {
    if (this.tipTimer !== undefined) clearInterval(this.tipTimer);
    this.tipTimer = undefined;
  }
}

/** Keeps where a zone change is heading across its reload (index.html shows it before the code runs). */
export function rememberDestination(text: string): void {
  try {
    sessionStorage.setItem(DEST_KEY, JSON.stringify({ dest: text }));
  } catch {
    // Storage blocked: the line just shows a moment later, once the code runs.
  }
}

function clearDestination(): void {
  try {
    sessionStorage.removeItem(DEST_KEY);
  } catch {
    // ignore
  }
}
